import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { VAN_DIJK } from '@/demo/data'

/**
 * Demo slice — the CONCEPT-INTERACTION act log, and nothing else.
 *
 * Architecture: an APPEND-ONLY act log; everything else is derived on read via
 * selectors (the bkt-poc invention — there is no revoke code path to miss).
 * The frozen seed stays OUT of the store: the store holds only what a click on
 * a concept surface can change, plus ids referencing the seed.
 *
 * ⚠ PX-W6.6 (TASK-188E6E40, spec §6.3 item 7) retired this slice from every
 * product path. `currentRole`, `signedInAs` and the `roleChanged` / `signedIn`
 * / `signedOut` actions are GONE: the identity a surface renders and the role
 * `canAccess` gates on now come from the REAL session
 * (`features/auth/slices/sessionSlice`, `selectActiveRoleKey`). Do not
 * reintroduce a session field here — `src/demo/demoBoundary.test.ts` and the
 * store audit both fail on a product surface reaching back into this slice.
 *
 * What remains is exactly what the three badged concept surfaces write:
 * a module review on the build board, the ACTIE_CODE split acceptance on the
 * migration surface, and a requirement approval on the requirements register.
 *
 * Reset: dispatch `resetAll` (handled by a root wrapper in store/index.ts) —
 * wired to a visible control in the shell.
 */

export type ReviewVerdict = 'accept' | 'accept-with-edits' | 'reject'

export type DemoAct =
  | { kind: 'module-review'; module: string; verdict: ReviewVerdict; reason?: string; by: string; atLabel: string }
  | { kind: 'split-accepted'; field: 'ACTIE_CODE'; rows: number; by: string; atLabel: string }
  | { kind: 'requirement-approved'; requirement: number; by: string; atLabel: string }

export interface DemoState {
  acts: DemoAct[]
}

const initialState: DemoState = {
  acts: [],
}

export const demoSlice = createSlice({
  name: 'demo',
  initialState,
  reducers: {
    /** The one write path: acts are appended, never edited, never removed. */
    actRecorded(state, action: PayloadAction<DemoAct>) {
      state.acts.push(action.payload)
    },
  },
})

export const { actRecorded } = demoSlice.actions

/* ── Selectors (derive, never store) ────────────────────────────────────── */

interface WithDemo {
  demo: DemoState
}

export const selectActs = (s: WithDemo) => s.demo.acts

/** Latest review verdict per module (later acts win — the log is the truth). */
export const selectModuleReviews = createSelector([selectActs], (acts) => {
  const byModule: Record<string, { verdict: ReviewVerdict; reason?: string; by: string; atLabel: string }> = {}
  for (const act of acts) {
    if (act.kind === 'module-review') {
      byModule[act.module] = { verdict: act.verdict, reason: act.reason, by: act.by, atLabel: act.atLabel }
    }
  }
  return byModule
})

/**
 * Build progress projects forward from the pristine seed (18/26) + accepted
 * reviews. Surfaces 3.1 and 3.5 both read THIS, so a merge on the review
 * queue moves the overview's KPI with it.
 */
export const selectBuildProgress = createSelector([selectModuleReviews], (reviews) => {
  const accepted = Object.values(reviews).filter(
    (r) => r.verdict === 'accept' || r.verdict === 'accept-with-edits'
  ).length
  return {
    done: VAN_DIJK.build.modulesDone + accepted,
    total: VAN_DIJK.build.modulesTotal,
  }
})

/** Whether the ACTIE_CODE split has been accepted (surface 3.6 + 3.1 read it). */
export const selectSplitAccepted = createSelector([selectActs], (acts) =>
  acts.find((a) => a.kind === 'split-accepted')
)

/** Migration object counts projected from the pristine seed + the split act. */
export const selectMigrationObjects = createSelector([selectSplitAccepted], (split) => {
  const base = VAN_DIJK.migration.objects
  if (!split) return base
  return { ...base, mapped: base.mapped + 1, inReview: base.inReview - 1 }
})

/** Requirements approved: pristine 128/142 + any approval acts. */
export const selectRequirements = createSelector([selectActs], (acts) => {
  const approvals = acts.filter((a) => a.kind === 'requirement-approved').length
  const base = VAN_DIJK.requirements
  return { total: base.total, approved: base.approved + approvals, open: base.open - approvals }
})
